// @flow

import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";

import { ReactComponent as Triangle } from "../../../assets/images/shape-triangle.svg";
import { ReactComponent as Ring } from "../../../assets/images/shape-ring-thick-fill.svg";

import Config from "../../constants/Config";
import actions from "../../actions/";
import LinkHandler from "../atoms/LinkHandler";
import Icon from "../atoms/Icon";
import Modal from "../organisms/Modal";
import TextButton from "../atoms/TextButton";

type PropsType = {
    active: boolean,
    onClose: Function,
};

const AccountInfoModal = props => {
    const { profile } = useSelector((state: any): any => state.user);

    const isGeneralAccess = !profile.type || profile.type === "public";
    let accountCopy;
    if (profile.type === "staff") {
        accountCopy =
            "You’re logged in as a UTS staff member, which gives you staff prices on event tickets and club memberships and 10% off UTS merch.";
    } else if (profile.type === "alumni") {
        accountCopy =
            "You are logged in as a UTS Alumni, which gives you alumni prices on event tickets and club memberships.";
    } else {
        accountCopy = `You're logged in as a UTS ${profile.type}, which
        gives you access to ${profile.type} prices on
        event tickets and club memberships and 10% off
        UTS merch.`;
    }
    return (
        <Modal
            aside={true}
            active={props.active}
            className="account-modal--info"
            onClose={props.onClose}
            onOverlayClick={props.onClose}
        >
            <Modal.Header>
                <Icon name="chevron-left" onClick={props.onClose} />
            </Modal.Header>
            <Modal.Content>
                <div className="container">
                    <h5 className="heading-5">Account info</h5>
                    <p>
                        {isGeneralAccess ? (
                            <>
                                You're logged in with general access to the
                                ActivateUTS website. If you have a UTS ID, go to
                                the link below to link your UTS credentials for
                                access to UTS discount prices.
                            </>
                        ) : (
                            <>{accountCopy}</>
                        )}
                    </p>

                    {isGeneralAccess ? (
                        <>
                            <TextButton
                                onClick={() => props.handleChangeAccountType()}
                            >
                                Change account type
                            </TextButton>
                        </>
                    ) : null}
                </div>
            </Modal.Content>
        </Modal>
    );
};

const AccountModal = (props: PropsType): React.Node => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const history = useHistory();
    const [accountInfoActive, setAccountInfoActive] = React.useState(false);
    const { profile } = useSelector((state: any): any => state.user);
    const isGeneralAccess = !profile.type || profile.type === "public";
    const isClubExec =
        profile && profile.role
            ? profile.role.filter(role => role === "club_exec").length > 0
            : false;

    const isEAPMember = profile.elite_level && profile.elite_level.name;
    const canManageEvents =
        profile.permissions &&
        profile.permissions.includes("auts_event_checkin_access");

    const handleCloseAccountInfoModal = () => {
        dispatch(actions.openAccountModal());

        setAccountInfoActive(false);
    };

    const handleOpenAccountInfoModal = () => {
        if (props.onClose) {
            props.onClose();
        }
        setAccountInfoActive(true);
    };

    const handleChangeAccountType = () => {
        if (props.onClose) {
            props.onClose();
        }
        setAccountInfoActive(false);
        history.push(Config.routes.editProfile);
    };
    return (
        <>
            <AccountInfoModal
                active={accountInfoActive}
                onClose={handleCloseAccountInfoModal}
                handleChangeAccountType={handleChangeAccountType}
            />

            <Modal
                aside={true}
                active={props.active}
                className="account-modal"
                onClose={props.onClose}
                onOverlayClick={props.onClose}
            >
                <Modal.Header>
                    {profile ? (
                        <>
                            <div className="modal__header__content__inner">
                                <Ring className="account-modal__ring" />
                                <Triangle className="account-modal__triangle" />
                                <h3 className="account-modal__title">
                                    <span>Hey </span>
                                    {profile.preferred_name ||
                                        profile.first_name}
                                </h3>

                                <button
                                    className="account-modal__profile-type-btn"
                                    onClick={handleOpenAccountInfoModal}
                                >
                                    {isGeneralAccess
                                        ? "General access"
                                        : profile.type}
                                    <Icon name="help" />
                                </button>
                            </div>
                            <div className="modal__header__content__gap" />
                        </>
                    ) : null}
                </Modal.Header>
                <Modal.Content>
                    {profile ? (
                        <>
                            <div className="account-modal__content__inner">
                                <LinkHandler
                                    className="account-modal__link"
                                    activeClassName="account-modal__link--active"
                                    to={Config.routes.incomingTickets}
                                >
                                    <span className="account-modal__link__title">
                                        Tickets
                                    </span>
                                </LinkHandler>

                                <LinkHandler
                                    className="account-modal__link"
                                    activeClassName="account-modal__link--active"
                                    to={Config.routes.currentMembership}
                                >
                                    <span className="account-modal__link__title">
                                        Club memberships
                                    </span>
                                </LinkHandler>

                                {/* EAP landing/benefits pages */}
                                {isEAPMember ? (
                                    <>
                                        <LinkHandler
                                            className="account-modal__link"
                                            activeClassName="account-modal__link--active"
                                            to={Config.routes.eapLanding}
                                            exact={true}
                                        >
                                            <span className="account-modal__link__title">
                                                Elite Athlete Program
                                            </span>
                                        </LinkHandler>

                                        <LinkHandler
                                            className="account-modal__link"
                                            activeClassName="account-modal__link--active"
                                            to={Config.routes.eapBenefits}
                                            exact={true}
                                        >
                                            <span className="account-modal__link__title">
                                                Your scholarship
                                            </span>
                                        </LinkHandler>
                                    </>
                                ) : null}

                                {canManageEvents ? (
                                    <LinkHandler
                                        isActive={() =>
                                            [
                                                Config.routes.managedEvents,
                                                Config.routes.managedEvent,
                                            ].find(route =>
                                                pathname.includes(route)
                                            )
                                        }
                                        className="account-modal__link"
                                        activeClassName="account-modal__link--active"
                                        to={Config.routes.managedEvents}
                                    >
                                        <span className="account-modal__link__title">
                                            Manage your events
                                        </span>
                                    </LinkHandler>
                                ) : null}

                                {isClubExec ? (
                                    <LinkHandler
                                        isActive={() =>
                                            [
                                                Config.routes.manageClubs,
                                                Config.routes.manageEvent,
                                            ].find(route =>
                                                pathname.includes(route)
                                            )
                                        }
                                        className="account-modal__link"
                                        activeClassName="account-modal__link--active"
                                        to={Config.routes.manageClubs}
                                    >
                                        <span className="account-modal__link__title">
                                            Manage your clubs
                                        </span>
                                    </LinkHandler>
                                ) : null}

                                {/* <LinkHandler
                        exact
                        className="account-modal__link"
                        activeClassName="account-modal__link--active"
                         to="/profile/resources/clubs"
                    >
                        <span className="account-modal__link__title">
                            Club management resources
                        </span>
                    </LinkHandler> */}

                                {/* <LinkHandler
                        exact
                        className="account-modal__link"
                        activeClassName="account-modal__link--active"
                        to="/profile/tickets"
                    >
                        <span className="account-modal__link__title">
                            Elite Athlete Program
                        </span>
                    </LinkHandler> */}

                                {/* <LinkHandler
                        exact
                        className="account-modal__link"
                        activeClassName="account-modal__link--active"
                        to="/profile/tickets"
                    >
                        <span className="account-modal__link__title">
                            Student leadership program
                        </span>
                    </LinkHandler> */}

                                <LinkHandler
                                    className="account-modal__link"
                                    activeClassName="account-modal__link--active"
                                    to={Config.routes.purchases}
                                >
                                    <span className="account-modal__link__title">
                                        Purchases
                                    </span>
                                </LinkHandler>

                                <LinkHandler
                                    exact
                                    className="account-modal__link"
                                    activeClassName="account-modal__link--active"
                                    to={Config.routes.editProfile}
                                >
                                    <span className="account-modal__link__title">
                                        Edit profile
                                    </span>
                                </LinkHandler>

                                <LinkHandler
                                    exact
                                    className="account-modal__link logout"
                                    activeClassName="account-modal__link--active"
                                    to={Config.routes.logout}
                                >
                                    <Icon name="logout" />
                                    <span className="account-modal__link__title">
                                        Logout
                                    </span>
                                </LinkHandler>
                            </div>
                        </>
                    ) : null}
                </Modal.Content>
            </Modal>
        </>
    );
};

export default AccountModal;
